import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { setParentOrigin } from '@/utils/parentOrigin';

const RENDER_DATA_TIMEOUT_MS = 10_000;

export const IframeRenderDataContext = createContext<unknown>(null);

export const IframeRenderDataProvider = ({ children }: { children: ReactNode }) => {
  const [renderData, setRenderData] = useState<unknown>(null);
  const [timedOut, setTimedOut] = useState(false);
  const receivedRef = useRef(false);

  useEffect(() => {
    const handleMessage = (
      event: MessageEvent<{ type?: string; payload?: { renderData?: unknown } }>
    ) => {
      if (event.data.type === 'ui-lifecycle-iframe-render-data') {
        const incomingRenderData = event.data.payload?.renderData;
        if (incomingRenderData) {
          setParentOrigin(event.origin);
          receivedRef.current = true;
          setRenderData(incomingRenderData);
        }
      }
      // Note: ui-field-update messages are not handled here.
      // The old onFieldUpdate pattern is being deprecated in favor of
      // direct state management within components.
    };

    window.addEventListener('message', handleMessage);

    if (window.parent !== window) {
      window.parent.postMessage({ type: 'ui-request-render-data' }, '*');
    }

    const timeout = setTimeout(() => {
      if (!receivedRef.current) {
        setTimedOut(true);
      }
    }, RENDER_DATA_TIMEOUT_MS);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeout);
    };
  }, []);

  if (timedOut) {
    throw new Error('Iframe render data not received within timeout');
  }

  return (
    <IframeRenderDataContext.Provider value={renderData}>
      {children}
    </IframeRenderDataContext.Provider>
  );
};

export function useIframeRenderData<T = unknown>(): T | null {
  return useContext(IframeRenderDataContext) as T | null;
}
